/**
 * Vault page → "Recovery" card (Phase 9c). Shows honestly whether Aurix support can restore this
 * vault, and lets the user turn it on/off or replace a backup that belongs to another vault.
 */
import { APP_SHORT_NAME } from "@custos/core";
import { Badge, Button, Card, CardTitle, formatDateTime, Modal, Notice } from "@custos/ui";
import { useState } from "react";
import { Link } from "react-router";
import { errorMessage } from "../lib/errors";
import { useVaultRecovery } from "../lib/queries";
import { disableRecovery, sameVault, syncRecoveryBackup, useVault } from "../lib/vault";

export function VaultRecoveryCard() {
  const recovery = useVaultRecovery();
  const accounts = useVault((s) => s.accounts);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const info = recovery.data;
  if (!info || !info.available) return null;

  const backup = info.backup;
  const on = backup?.enabled === true;
  const matches = on && sameVault(backup.addresses, accounts);
  const open = info.request && ["REQUESTED", "APPROVED", "READY"].includes(info.request.status);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-4" data-testid="vault-recovery-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>Recovery by {APP_SHORT_NAME} support</CardTitle>
        {matches ? (
          <Badge tone="success">On · backed up</Badge>
        ) : on ? (
          <Badge tone="warning">Backup is of another vault</Badge>
        ) : (
          <Badge tone="neutral">Off</Badge>
        )}
      </div>
      <p className="mt-2 text-sm text-muted">
        {matches ? (
          <>
            If you lose your recovery phrase and this device, support can restore this vault after checking
            your identity and a {info.waitHours}-hour waiting period. The copy is encrypted for the{" "}
            {APP_SHORT_NAME} recovery key, which is kept offline — not on our servers. Backed up{" "}
            {formatDateTime(backup.updatedAt)}.
          </>
        ) : on ? (
          <>
            The recovery backup on your account belongs to a different vault (for example one on another
            device). Only one vault per account can be recoverable.
          </>
        ) : (
          <>
            Only your recovery phrase / private keys can restore this vault. Lose them and the funds are gone.
          </>
        )}
      </p>
      {error !== null && (
        <Notice tone="danger" className="mt-3">
          {errorMessage(error)}
        </Notice>
      )}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        {matches && (
          <Button variant="secondary" disabled={busy || !!open} onClick={() => setConfirmOff(true)}>
            Turn recovery off
          </Button>
        )}
        {on && !matches && (
          <Button loading={busy} onClick={() => void run(() => syncRecoveryBackup({ replace: true }))}>
            Make THIS vault recoverable
          </Button>
        )}
        {!on && (
          <Button
            loading={busy}
            onClick={() => void run(() => syncRecoveryBackup({ replace: true, turnOn: true }))}
          >
            Turn recovery on
          </Button>
        )}
        {open && (
          <Link to="/vault/recover" className="self-center text-sm text-primary-strong hover:underline">
            A recovery request is open — view it
          </Link>
        )}
      </div>

      <Modal open={confirmOff} onOpenChange={setConfirmOff} title="Turn recovery off?">
        <div className="flex flex-col gap-4">
          <Notice tone="warning">
            Our encrypted copy will be deleted. From then on ONLY your recovery phrase / private keys can
            restore this vault — support won't be able to help.
          </Notice>
          <Button
            variant="danger"
            block
            loading={busy}
            onClick={() =>
              void run(async () => {
                await disableRecovery();
                setConfirmOff(false);
              })
            }
          >
            Turn off and delete the copy
          </Button>
        </div>
      </Modal>
    </Card>
  );
}
